/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import type { UIconName } from './UIconName';
import { paths } from './paths';

export interface UIconProps extends Omit<
    React.SVGProps<SVGSVGElement>,
    'width' | 'height'
> {
    /**
     * The name of the icon to render. See UIconName for all supported icons.
     */
    name: UIconName;
    /**
     * The size (width and height) of the icon in pixels.
     * @default 24
     */
    size?: number;
    /**
     * The stroke width of the icon lines. Scales with size by default, so the
     * icon looks consistent at different sizes. Set absoluteStrokeWidth to true
     * to make this an absolute pixel value instead.
     * @default 2
     */
    strokeWidth?: number;
    /**
     * If true, the strokeWidth will be treated as an absolute pixel value
     * instead of scaling with the icon size.
     * @default false
     */
    absoluteStrokeWidth?: boolean;
    /**
     * Accessible label for the icon. If provided, the SVG will have role="img" and
     * this label will be used by screen readers. If not provided, the SVG will be
     * decorative (aria-hidden) and ignored by assistive technologies.
     */
    'aria-label'?: string;
}

/**
 * Single shared icon component. Renders one SVG wrapper with icon-specific path
 * content. Use size (not width/height).
 */
export const UIcon = React.forwardRef<SVGSVGElement, UIconProps>(
    (props, forwardedRef) => {
        const {
            name,
            size = 24,
            strokeWidth = 2,
            absoluteStrokeWidth = false,
            'aria-label': ariaLabel,
            ...componentProps
        } = props;

        const computedStrokeWidth =
            absoluteStrokeWidth && size > 0 ? strokeWidth * (24 / size) : strokeWidth;

        const isDecorative = !ariaLabel;

        return (
            <svg
                {...componentProps}
                ref={forwardedRef}
                width={size}
                height={size}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={computedStrokeWidth}
                strokeLinecap="round"
                strokeLinejoin="round"
                role={isDecorative ? undefined : 'img'}
                aria-label={ariaLabel}
                aria-hidden={isDecorative ? true : undefined}
                data-component="UIcon"
            >
                {paths[name]}
            </svg>
        );
    }
);

UIcon.displayName = 'UIcon';
